// src/components/FeatureGate.js
import React from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Lock, Crown, AlertCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const FeatureGate = ({
  children,
  feature,
  fallback = null,
  showUpgradeMessage = true,
  className = ""
}) => {
  const {
    isFeatureAvailable,
    getUpgradeMessage,
    canUpgrade,
    isTrialActive,
  } = useSubscription();

  const hasFeature = isFeatureAvailable(feature);
  const trialActive = isTrialActive();

  if (hasFeature) {
    return <>{children}</>;
  }

  if (fallback) {
    return fallback;
  }

  if (!showUpgradeMessage) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            {trialActive ? (
              <Clock className="h-5 w-5 text-blue-600" />
            ) : (
              <Lock className="h-5 w-5 text-blue-600" />
            )}
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {trialActive ? 'Trial Feature' : 'Feature Not Available'}
          </h3>
          <p className="text-gray-600 mb-4">
            {getUpgradeMessage(feature)}
          </p>
          {canUpgrade() && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/subscription"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Crown className="h-4 w-4" />
                {trialActive ? 'Subscribe Now' : 'Upgrade Plan'}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
              >
                View Pricing
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const LimitGate = ({
  children,
  type,
  current,
  fallback = null,
  showUpgradeMessage = true,
  className = ""
}) => {
  const {
    isWithinLimits,
    getCurrentPlan,
    getUpgradeMessage,
    canUpgrade,
    isTrialActive,
    getTrialDaysRemaining,
    currentSubscription
  } = useSubscription();

  const withinLimits = isWithinLimits(type, current);
  const currentPlan = getCurrentPlan();
  const trialActive = isTrialActive();
  const trialDaysRemaining = getTrialDaysRemaining();

  // If trial is active, use pro limits
  const effectiveLimits = trialActive ? 'pro' : currentPlan;

  if (withinLimits) {
    return <>{children}</>;
  }

  if (fallback) {
    return fallback;
  }

  if (!showUpgradeMessage) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            {trialActive ? (
              <Clock className="h-5 w-5 text-amber-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-600" />
            )}
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {trialActive ? 'Trial Limit' : 'Limit Reached'}
          </h3>
          <p className="text-gray-600 mb-4">
            {trialActive
              ? `Your free trial ends in ${trialDaysRemaining} days. Upgrade to continue without limits after your trial ends.`
              : `You've reached your ${type} limit for the ${currentPlan} plan. ${getUpgradeMessage(type)}.`
            }
          </p>
          {canUpgrade() && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/subscription"
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Crown className="h-4 w-4" />
                {trialActive ? 'Subscribe Now' : 'Upgrade Plan'}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-amber-600 text-amber-600 px-4 py-2 rounded-lg hover:bg-amber-50 transition-colors"
              >
                View Pricing
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureGate;